import { useVideoExportContext } from "@/animation/video-generator/components/VideoExportContext";
import { isVideoExportRecordingActive } from "@/animation/video-generator/exportActivity";
import { getFontEmbedCssForElement } from "@/animation/video-generator/generator/exportFontEmbed";
import { createSnapshotNodeFilter } from "@/animation/video-generator/generator/snapshotNodeFilter";
import { withRasterizedSvgs } from "@/animation/video-generator/generator/svgRasterize";
import { compositor } from "@/layers/Compositor";
import type { MapboxMap } from "@/layers/MapboxMap";
import {
  PLOT_RECORDING_OVERLAY_REDRAW_EVENT,
  PLOT_RECORDING_PREPARE_SNAPSHOT_EVENT,
} from "@/overlay/components/CommonParts/PlotHighchart/recordingEvents";
import {
  CROSS_SECTION_RECORDING_OVERLAY_REDRAW_EVENT,
  CROSS_SECTION_RECORDING_PREPARE_SNAPSHOT_EVENT,
} from "@/overlay/components/CrossSectionCore/recordingEvents";
import { useCountryPlots } from "@/reducer/CountryPlotState";
import { useCrossSections } from "@/reducer/CrossSectionState";
import { useEnergyPlots } from "@/reducer/EnergyPlotsState";
import { useLocationTables } from "@/reducer/LocationTablesState";
import { useMaps } from "@/reducer/MapsState";
import { useNotes } from "@/reducer/NoteState";
import { usePlots } from "@/reducer/PlotsState";
import { useTephigrams } from "@/reducer/TephigramState";
import { useWeatherTables } from "@/reducer/WeatherTablesState";
import type { Snapshot } from "@/threads/MediaExportThread/MediaExportThreadPoolMsg";
import Logger from "@mm/logging";
import { useCallback, useEffect, useRef } from "react";

const DEFAULT_EXPORT_UI_PIXEL_RATIO = 1;
const QUICK_ACCESS_LABEL_CLASS = "plot-label";
const PLOT_RECORDING_OVERLAY_CANVAS_CLASS = "plot-recording-overlay-canvas";
const CROSS_SECTION_RECORDING_OVERLAY_CANVAS_CLASS = "cross-section-recording-overlay-canvas";
const PLOT_SERIES_SELECTOR = ".highcharts-series-group .highcharts-series";
const PLOT_GRAPH_SELECTOR = "path.highcharts-graph, path.highcharts-area";
const PLOT_POINT_SELECTOR = ".highcharts-point";
const PLOT_AREA_SELECTOR = ".highcharts-plot-background, .highcharts-plot-border";
const MAP_RECORDING_LEGEND_GRAPHIC_SELECTOR = '[data-recording-map-legend-graphic="true"]';
const MAP_RECORDING_STATIC_OVERLAY_TYPE = "map-legend";
const MIN_PLOT_CLIP_WIDTH_RATIO = 0.9;
const MIN_PLOT_VISIBLE_POINT_COUNT = 2;
const PLOT_READY_SETTLE_FRAMES = 4;
const STATIC_OVERLAY_READY_SETTLE_FRAMES = 2;
const MAPBOX_LOADED_FLAG_WAIT_MS = 300;

const logger = Logger.fromFilename(__filename);

type CachedStaticSnapshot = {
  height: number;
  snapshot: Blob;
  width: number;
};

async function elementToBlob(element: HTMLElement, options: Parameters<typeof import("html-to-image").toBlob>[1]) {
  const { toBlob } = await import("html-to-image");
  // By default html-to-image embeds every @font-face of the page (incl. ~12 MB of Material Symbols)
  // into each capture. Only embed the fonts this element really renders with.
  const fontEmbedCSS = await getFontEmbedCssForElement(element);
  return withRasterizedSvgs(element, () =>
    toBlob(element, {
      ...options,
      fontEmbedCSS,
      filter: createSnapshotNodeFilter(options?.filter),
    }),
  );
}

async function elementToCanvas(element: HTMLElement, options: Parameters<typeof import("html2canvas").default>[1]) {
  const { default: html2canvas } = await import("html2canvas");
  return html2canvas(element, options);
}

function waitForNextRenderAsync(map: MapboxMap): Promise<void> {
  return new Promise((resolve) => {
    map.once("render", () => resolve());
    map.triggerRepaint();
  });
}

function getExportUiPixelRatio(): number {
  // Keep non-map UI snapshots at DPR=1 to bound DOM capture cost.
  // Map imagery still keeps full canvas/device resolution in a separate path.
  return DEFAULT_EXPORT_UI_PIXEL_RATIO;
}

/**
 * For non-map tools, capture the full tool cell container (closest `.overlay`)
 * so content and inline labels are composited in one snapshot.
 * Falls back to the element itself if no overlay parent exists.
 */
function getCompositeToolContainer(element?: HTMLElement | null): HTMLElement | null {
  if (!element) {
    return null;
  }
  return (element.closest(".overlay") as HTMLElement | null) ?? element;
}

/**
 * Returns the concrete HTML nodes to snapshot for overlay labels.
 *
 * Map/tephigram overlays are captured as full overlays.
 * Non-map overlays are narrowed to quick-access labels where available to avoid
 * duplicating full-cell captures.
 */
function getOverlaySnapshotTargets(className: SnapshotLabelHtmlClassNames, overlay: HTMLElement): HTMLElement[] {
  if (className === SnapshotLabelHtmlClassNames.Map || className === SnapshotLabelHtmlClassNames.Tephigram) {
    return [overlay];
  }

  const labels = overlay.getElementsByClassName(QUICK_ACCESS_LABEL_CLASS);
  if (labels.length > 0) {
    return Array.from(labels) as HTMLElement[];
  }

  return [overlay];
}

function waitForAnimationFramesAsync(frameCount: number): Promise<void> {
  return new Promise((resolve) => {
    const tick = (remainingFrames: number) => {
      if (remainingFrames <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => tick(remainingFrames - 1));
    };

    tick(frameCount);
  });
}

function getVisibleSvgRects(root: ParentNode, selector: string): DOMRect[] {
  return Array.from(root.querySelectorAll(selector)).flatMap((node) => {
    if (!(node instanceof SVGGraphicsElement)) {
      return [];
    }

    const rect = node.getBoundingClientRect();
    if (rect.width <= 0 && rect.height <= 0) {
      return [];
    }

    return [rect];
  });
}

function getSeriesClipWidth(plotRoot: HTMLElement, seriesElement: Element): number | null {
  const clipPathValue = seriesElement.getAttribute("clip-path");
  const clipPathId = clipPathValue?.match(/url\(#([^)]+)\)/)?.[1];
  if (!clipPathId) {
    return null;
  }

  const clipRect = plotRoot.querySelector(`clipPath[id="${clipPathId}"] rect`);
  if (!(clipRect instanceof SVGRectElement)) {
    return null;
  }

  const width = Number.parseFloat(clipRect.getAttribute("width") ?? "");
  return Number.isFinite(width) ? width : null;
}

function hasRenderablePlotData(plotRoot: HTMLElement): boolean {
  // A visible weather/asset line can precede the remaining portfolio requests.
  if (plotRoot.getAttribute("aria-busy") === "true" || plotRoot.querySelector('[aria-busy="true"]')) {
    return false;
  }
  const plotAreaRect = getVisibleSvgRects(plotRoot, PLOT_AREA_SELECTOR)[0];
  if (!plotAreaRect || plotAreaRect.width <= 0) {
    return false;
  }

  const minClipWidth = plotAreaRect.width * MIN_PLOT_CLIP_WIDTH_RATIO;
  const seriesElements = plotRoot.querySelectorAll(PLOT_SERIES_SELECTOR);

  return Array.from(seriesElements).some((seriesElement) => {
    const clipWidth = getSeriesClipWidth(plotRoot, seriesElement);
    if (clipWidth === null || clipWidth < minClipWidth) {
      return false;
    }

    const hasGraph = seriesElement.querySelector(PLOT_GRAPH_SELECTOR) !== null;
    if (hasGraph) {
      return true;
    }

    return seriesElement.querySelectorAll(PLOT_POINT_SELECTOR).length >= MIN_PLOT_VISIBLE_POINT_COUNT;
  });
}

function waitForRenderablePlotDataAsync(plotRoot: HTMLElement, maxWaitMs = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    if (hasRenderablePlotData(plotRoot)) {
      void waitForAnimationFramesAsync(PLOT_READY_SETTLE_FRAMES).then(() => resolve(hasRenderablePlotData(plotRoot)));
      return;
    }

    let timeoutId: number | null = null;
    let rafId: number | null = null;
    let readyPromise: Promise<void> | null = null;

    const cleanup = () => {
      observer.disconnect();
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };

    const finish = (isReady: boolean) => {
      cleanup();
      resolve(isReady);
    };

    const scheduleCheck = () => {
      if (rafId !== null || readyPromise) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;

        if (!hasRenderablePlotData(plotRoot)) {
          return;
        }

        readyPromise = waitForAnimationFramesAsync(PLOT_READY_SETTLE_FRAMES).then(() => {
          readyPromise = null;
          finish(hasRenderablePlotData(plotRoot));
        });
      });
    };

    const observer = new MutationObserver(scheduleCheck);
    observer.observe(plotRoot, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    scheduleCheck();
    timeoutId = window.setTimeout(() => finish(hasRenderablePlotData(plotRoot)), maxWaitMs);
  });
}

function hasRenderableStaticOverlayContent(target: HTMLElement): boolean {
  const overlayType = target.dataset.recordingStaticOverlayType;

  if (overlayType === MAP_RECORDING_STATIC_OVERLAY_TYPE) {
    const expectedCount = Number.parseInt(target.dataset.recordingStaticOverlayExpectedCount ?? "", 10);
    if (!Number.isFinite(expectedCount) || expectedCount <= 0) {
      return false;
    }

    const readyLegends = Array.from(target.querySelectorAll(MAP_RECORDING_LEGEND_GRAPHIC_SELECTOR)).filter((node) => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      if (node.childElementCount === 0) {
        return false;
      }

      const rect = node.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    return readyLegends.length >= expectedCount;
  }

  return true;
}

function waitForRenderableStaticOverlayAsync(target: HTMLElement, maxWaitMs = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    if (hasRenderableStaticOverlayContent(target)) {
      void waitForAnimationFramesAsync(STATIC_OVERLAY_READY_SETTLE_FRAMES).then(() =>
        resolve(hasRenderableStaticOverlayContent(target)),
      );
      return;
    }

    let timeoutId: number | null = null;
    let rafId: number | null = null;
    let readyPromise: Promise<void> | null = null;

    const cleanup = () => {
      observer.disconnect();
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };

    const finish = (isReady: boolean) => {
      cleanup();
      resolve(isReady);
    };

    const scheduleCheck = () => {
      if (rafId !== null || readyPromise) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;

        if (!hasRenderableStaticOverlayContent(target)) {
          return;
        }

        readyPromise = waitForAnimationFramesAsync(STATIC_OVERLAY_READY_SETTLE_FRAMES).then(() => {
          readyPromise = null;
          finish(hasRenderableStaticOverlayContent(target));
        });
      });
    };

    const observer = new MutationObserver(scheduleCheck);
    observer.observe(target, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    scheduleCheck();
    timeoutId = window.setTimeout(() => finish(hasRenderableStaticOverlayContent(target)), maxWaitMs);
  });
}

type MapboxReadiness = {
  loaded: boolean;
  moving: boolean;
  styleLoaded: boolean;
  tilesLoaded: boolean;
};

function getMapboxReadiness(map: MapboxMap): MapboxReadiness {
  return {
    loaded: map.loaded?.() ?? true,
    moving: map.isMoving?.() ?? false,
    styleLoaded: map.isStyleLoaded?.() ?? true,
    tilesLoaded: map.areTilesLoaded?.() ?? true,
  };
}

/**
 * Waits until `isReady` holds (checked on every render/idle event) or `maxWaitMs` elapsed.
 * Resolves with whether the condition was met.
 */
function waitForMapboxConditionAsync(map: MapboxMap, isReady: () => boolean, maxWaitMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (isReady()) {
      resolve(true);
      return;
    }

    let timeoutId: number | null = null;

    const cleanup = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      map.off("render", onSignal);
      map.off("idle", onSignal);
    };

    const onSignal = () => {
      if (isReady()) {
        cleanup();
        resolve(true);
      }
    };

    map.on("render", onSignal);
    map.on("idle", onSignal);
    map.triggerRepaint();

    timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(false);
    }, maxWaitMs);
  });
}

/**
 * Waits for the real readiness signals (map not moving, style and tiles loaded) for up to
 * `maxWaitMs`, exactly as before, so slow layers/tiles are still awaited.
 * `map.loaded()` alone only gets a short grace period afterwards: with our custom layers it can
 * stay false permanently even though style and tiles are done, which used to add the full timeout
 * to every export.
 */
async function waitForMapboxReadyAsync(
  map: MapboxMap,
  maxWaitMs = 2000,
  maxLoadedFlagWaitMs = MAPBOX_LOADED_FLAG_WAIT_MS,
) {
  const isSettled = () => {
    const { moving, styleLoaded, tilesLoaded } = getMapboxReadiness(map);
    return !moving && styleLoaded && tilesLoaded;
  };
  const isFullyLoaded = () => getMapboxReadiness(map).loaded;

  const settled = await waitForMapboxConditionAsync(map, isSettled, maxWaitMs);
  if (settled && !(await waitForMapboxConditionAsync(map, isFullyLoaded, maxLoadedFlagWaitMs))) {
    logger.debug(
      "Mapbox loaded() flag stayed false after style and tiles settled, continuing",
      getMapboxReadiness(map),
    );
  } else if (!settled) {
    logger.warn("Mapbox did not settle before snapshot, continuing", getMapboxReadiness(map));
  }
}

async function getMapSnapshotAsync(map: MapboxMap, containerRect?: DOMRect): Promise<ImageBitmap> {
  const dpr = window.devicePixelRatio;
  const canvas = map.getCanvas();
  const needsResize =
    !!containerRect &&
    (canvas.width !== Math.round(containerRect.width * dpr) ||
      canvas.height !== Math.round(containerRect.height * dpr));

  if (needsResize) {
    map.resize();
    // Ensure the resized canvas has been painted before capture.
    await waitForNextRenderAsync(map);
  }

  // Ensure mapbox finished processing pending style/tiles movement state before capturing.
  await waitForMapboxReadyAsync(map);
  // Capture directly on a render tick to avoid reading a stale frame.
  await waitForNextRenderAsync(map);
  return createImageBitmap(map.getCanvas());
}

export function generateIdForSnapshot(toolName: SnapshotToolHtmlIds, id: number) {
  return `${toolName}-${id}`;
}

export enum SnapshotLabelHtmlClassNames {
  Map = "map-grid-overlay",
  Plot = "plot-grid-overlay",
  CountryPlot = "country-plot-grid-overlay",
  EnergyPlot = "energy-plot-grid-overlay",
  MultiLocationTable = "multi-location-table-grid-overlay",
  WeatherTable = "weather-table-grid-overlay",
  Tephigram = "tephigram-grid-overlay",
  CrossSection = "cross-section-grid-overlay",
}

export enum SnapshotToolHtmlIds {
  Map = "map-grid-tool",
  Plot = "plot-grid-tool",
  CountryPlot = "country-plot-grid-tool",
  EnergyPlot = "energy-plot-grid-tool",
  MultiLocationTable = "multi-location-table-grid-tool",
  WeatherTable = "weather-table-grid-tool",
  Tephigram = "tephigram-grid-tool",
  CrossSection = "cross-section-grid-tool",
}

// No error handlings here
// TODO implement error handling
export const useToolSnapshotConstructor = (tabId: number) => {
  const { isVideoRecordingActive } = useVideoExportContext();
  const maps = useMaps(tabId);
  const plots = usePlots(tabId);
  const energyPlots = useEnergyPlots(tabId);
  const weatherTables = useWeatherTables(tabId);
  const locationTables = useLocationTables(tabId);
  const tephigrams = useTephigrams(tabId);
  const notes = useNotes(tabId);
  const countryPlots = useCountryPlots(tabId);
  const crossSections = useCrossSections(tabId);
  const plotBaseSnapshotCacheRef = useRef(new Map<number, CachedStaticSnapshot>());
  const staticOverlaySnapshotCacheRef = useRef(new Map<string, CachedStaticSnapshot>());

  useEffect(() => {
    if (!isVideoRecordingActive) {
      plotBaseSnapshotCacheRef.current.clear();
      staticOverlaySnapshotCacheRef.current.clear();
    }
  }, [isVideoRecordingActive]);

  const createSnapshotPromise = useCallback((container: HTMLElement, filter?: (node: HTMLElement) => boolean) => {
    return new Promise<Snapshot>((resolve, reject) => {
      requestAnimationFrame(async () => {
        try {
          const elementImage = await elementToBlob(container, {
            pixelRatio: getExportUiPixelRatio(),
            filter,
          });
          if (elementImage) {
            resolve({
              snapshot: elementImage,
              rect: container.getBoundingClientRect(),
            });
          } else {
            reject("Couldn't create blob!!!");
          }
        } catch (error) {
          reject(error);
        }
      });
    });
  }, []);

  const createVideoPlotSnapshotsAsync = useCallback(
    async (plotId: number): Promise<Snapshot[]> => {
      const plotRoot = document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.Plot, plotId));
      const plotContainer = getCompositeToolContainer(plotRoot);

      if (!plotRoot || !plotContainer) {
        return [];
      }

      const overlayCanvas = plotRoot.getElementsByClassName(PLOT_RECORDING_OVERLAY_CANVAS_CLASS)[0];
      if (!(overlayCanvas instanceof HTMLCanvasElement)) {
        return [await createSnapshotPromise(plotContainer)];
      }

      let containerRect = plotContainer.getBoundingClientRect();
      let roundedWidth = Math.round(containerRect.width);
      let roundedHeight = Math.round(containerRect.height);
      const cachedSnapshot = plotBaseSnapshotCacheRef.current.get(plotId);
      const hasMatchingCachedSnapshot =
        cachedSnapshot !== undefined &&
        cachedSnapshot.width === roundedWidth &&
        cachedSnapshot.height === roundedHeight &&
        hasRenderablePlotData(plotRoot);
      if (!hasMatchingCachedSnapshot) {
        plotBaseSnapshotCacheRef.current.delete(plotId);
      }

      let staticSnapshot = hasMatchingCachedSnapshot ? cachedSnapshot?.snapshot : undefined;
      if (!staticSnapshot) {
        const plotReady = await waitForRenderablePlotDataAsync(plotRoot);
        overlayCanvas.dispatchEvent(new Event(PLOT_RECORDING_PREPARE_SNAPSHOT_EVENT));
        await waitForAnimationFramesAsync(2);
        const baseSnapshot = await createSnapshotPromise(plotContainer, (node) => {
          return !node.classList?.contains(PLOT_RECORDING_OVERLAY_CANVAS_CLASS);
        });

        if (!(baseSnapshot.snapshot instanceof Blob)) {
          return [baseSnapshot];
        }

        staticSnapshot = baseSnapshot.snapshot;
        containerRect = baseSnapshot.rect;
        roundedWidth = Math.round(containerRect.width);
        roundedHeight = Math.round(containerRect.height);
        if (plotReady && hasRenderablePlotData(plotRoot)) {
          plotBaseSnapshotCacheRef.current.set(plotId, {
            snapshot: staticSnapshot,
            width: roundedWidth,
            height: roundedHeight,
          });
        }
      }

      overlayCanvas.dispatchEvent(new Event(PLOT_RECORDING_OVERLAY_REDRAW_EVENT));
      await waitForAnimationFramesAsync(1);
      containerRect = plotContainer.getBoundingClientRect();

      return [
        {
          snapshot: staticSnapshot,
          rect: containerRect,
        },
        {
          snapshot: await createImageBitmap(overlayCanvas),
          rect: overlayCanvas.getBoundingClientRect(),
        },
      ];
    },
    [createSnapshotPromise],
  );

  const createVideoCrossSectionSnapshotsAsync = useCallback(
    async (crossSectionId: number): Promise<Snapshot[]> => {
      const crossSectionRoot = document.getElementById(
        generateIdForSnapshot(SnapshotToolHtmlIds.CrossSection, crossSectionId),
      );
      const crossSectionContainer = getCompositeToolContainer(crossSectionRoot);

      if (!crossSectionRoot || !crossSectionContainer) {
        return [];
      }

      const overlayCanvas = crossSectionRoot.getElementsByClassName(CROSS_SECTION_RECORDING_OVERLAY_CANVAS_CLASS)[0];
      if (!(overlayCanvas instanceof HTMLCanvasElement)) {
        return [await createSnapshotPromise(crossSectionContainer)];
      }

      let containerRect = crossSectionContainer.getBoundingClientRect();
      let roundedWidth = Math.round(containerRect.width);
      let roundedHeight = Math.round(containerRect.height);
      const cachedSnapshot = plotBaseSnapshotCacheRef.current.get(crossSectionId);
      const hasMatchingCachedSnapshot =
        cachedSnapshot !== undefined &&
        cachedSnapshot.width === roundedWidth &&
        cachedSnapshot.height === roundedHeight;

      let staticSnapshot = cachedSnapshot?.snapshot;
      if (!staticSnapshot || !hasMatchingCachedSnapshot) {
        overlayCanvas.dispatchEvent(new Event(CROSS_SECTION_RECORDING_PREPARE_SNAPSHOT_EVENT));
        await waitForAnimationFramesAsync(2);
        const baseSnapshot = await createSnapshotPromise(crossSectionContainer, (node) => {
          return !node.classList?.contains(CROSS_SECTION_RECORDING_OVERLAY_CANVAS_CLASS);
        });

        if (!(baseSnapshot.snapshot instanceof Blob)) {
          return [baseSnapshot];
        }

        staticSnapshot = baseSnapshot.snapshot;
        containerRect = baseSnapshot.rect;
        roundedWidth = Math.round(containerRect.width);
        roundedHeight = Math.round(containerRect.height);
        plotBaseSnapshotCacheRef.current.set(crossSectionId, {
          snapshot: staticSnapshot,
          width: roundedWidth,
          height: roundedHeight,
        });
      }

      overlayCanvas.dispatchEvent(new Event(CROSS_SECTION_RECORDING_OVERLAY_REDRAW_EVENT));
      await waitForAnimationFramesAsync(1);
      containerRect = crossSectionContainer.getBoundingClientRect();

      return [
        {
          snapshot: staticSnapshot,
          rect: containerRect,
        },
        {
          snapshot: await createImageBitmap(overlayCanvas),
          rect: overlayCanvas.getBoundingClientRect(),
        },
      ];
    },
    [createSnapshotPromise],
  );

  /**
   * A callback to construct the blob for all the tools present in the current UI
   */
  const snapshotActiveToolsAsync = useCallback(() => {
    const snapshotPromises: Promise<Snapshot[]>[] = [];

    const mapSnapshotPromises: Promise<Snapshot[]>[] = [];
    for (const map of maps) {
      const scene = compositor.tryGetScene([map.id, map.id_profile, map.id_tab].join("."));
      const mapContainer = document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.Map, map.id));

      if (mapContainer && scene) {
        mapSnapshotPromises.push(
          (async () => {
            const mapboxMap = scene.getMapboxMap();
            const cRect = mapContainer.getBoundingClientRect();
            const snapshot = await getMapSnapshotAsync(mapboxMap, cRect);
            return [{ snapshot, rect: cRect }];
          })(),
        );
      }
    }
    snapshotPromises.push(...mapSnapshotPromises);

    for (const plot of plots) {
      const plotRoot = document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.Plot, plot.id));
      const plotContainer = getCompositeToolContainer(plotRoot);
      if (plotContainer) {
        if (isVideoExportRecordingActive()) {
          snapshotPromises.push(createVideoPlotSnapshotsAsync(plot.id));
        } else {
          snapshotPromises.push(
            (async () => {
              if (plotRoot?.classList.contains("plot-view--portfolio")) {
                await waitForRenderablePlotDataAsync(plotRoot);
              }
              return [await createSnapshotPromise(plotContainer)];
            })(),
          );
        }
      }
    }

    for (const energyPlot of energyPlots) {
      const plotContainer = getCompositeToolContainer(
        document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.EnergyPlot, energyPlot.id)),
      );
      if (plotContainer) {
        snapshotPromises.push(createSnapshotPromise(plotContainer).then((snapshot) => [snapshot]));
      }
    }

    for (const countryPlot of countryPlots) {
      const plotContainer = getCompositeToolContainer(
        document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.CountryPlot, countryPlot.id)),
      );
      if (plotContainer) {
        snapshotPromises.push(createSnapshotPromise(plotContainer).then((snapshot) => [snapshot]));
      }
    }

    for (const crossSection of crossSections) {
      const plotContainer = getCompositeToolContainer(
        document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.CrossSection, crossSection.id)),
      );
      if (plotContainer) {
        if (isVideoExportRecordingActive()) {
          snapshotPromises.push(createVideoCrossSectionSnapshotsAsync(crossSection.id));
        } else {
          snapshotPromises.push(createSnapshotPromise(plotContainer).then((snapshot) => [snapshot]));
        }
      }
    }

    for (const weatherTable of weatherTables) {
      const plotContainer = getCompositeToolContainer(
        document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.WeatherTable, weatherTable.id)),
      );
      if (plotContainer) {
        snapshotPromises.push(createSnapshotPromise(plotContainer).then((snapshot) => [snapshot]));
      }
    }

    for (const locationTable of locationTables) {
      const plotContainer = getCompositeToolContainer(
        document.getElementById(generateIdForSnapshot(SnapshotToolHtmlIds.MultiLocationTable, locationTable.id)),
      );
      if (plotContainer) {
        snapshotPromises.push(createSnapshotPromise(plotContainer).then((snapshot) => [snapshot]));
      }
    }

    // We still can not get the tephigram snapshot
    for (const tephigram of tephigrams) {
      const plotContainer = document.getElementById(`tephigram-${tephigram.id}`)?.parentElement;
      if (plotContainer) {
        snapshotPromises.push(createTephigramSnapshotPromise(plotContainer).then((snapshot) => [snapshot]));
      }
    }

    for (const note of notes) {
      const noteContainer = document.getElementById(`note-${note.id}`)?.parentElement;
      if (noteContainer) {
        snapshotPromises.push(createSnapshotPromise(noteContainer).then((snapshot) => [snapshot]));
      }
    }

    return Promise.all(snapshotPromises).then((groups) => groups.flat());
  }, [
    maps,
    energyPlots,
    plots,
    weatherTables,
    locationTables,
    tephigrams,
    notes,
    countryPlots,
    crossSections,
    createSnapshotPromise,
    createVideoPlotSnapshotsAsync,
    createVideoCrossSectionSnapshotsAsync,
  ]);

  const createTephigramSnapshotPromise = (container: HTMLElement) => {
    return new Promise<Snapshot>((resolve, reject) => {
      requestAnimationFrame(() => {
        elementToCanvas(container, {
          useCORS: true,
          allowTaint: true,
          scale: getExportUiPixelRatio(),
        })
          .then((canvas) => {
            canvas.toBlob((blob) => {
              if (blob) {
                resolve({
                  snapshot: blob,
                  rect: container.getBoundingClientRect(),
                });
              } else {
                reject("Canvas toBlob failed");
              }
            });
          })
          .catch((err) => {
            console.error("html2canvas error", err);
            reject(err);
          });
      });
    });
  };

  /**
   * A callback to construct the blob for map grid overlay.
   * The HTML elements selected by the provided class name contain supplemental visual elements like WMS legends.
   *
   * @param className The class name (from SnapshotLabelHtmlClassNames enum) used to find the overlay elements.
   * @returns A promise that resolves to an array of snapshots with their bounding rectangles.
   */
  const snapshotToolLabelOverlaysAsync = useCallback((className: SnapshotLabelHtmlClassNames) => {
    const overlays = document.getElementsByClassName(className);

    const mapOverlayPromises = Array.from(overlays).flatMap((overlay) => {
      const targets = getOverlaySnapshotTargets(className, overlay as HTMLElement);
      return targets.map((target) => {
        if (target instanceof HTMLCanvasElement) {
          return createImageBitmap(target).then((snapshot) => ({
            snapshot,
            rect: target.getBoundingClientRect(),
          }));
        }

        const staticOverlayKey = target.dataset.recordingStaticOverlayKey;
        const rect = target.getBoundingClientRect();
        const roundedWidth = Math.round(rect.width);
        const roundedHeight = Math.round(rect.height);
        if (staticOverlayKey) {
          const cachedSnapshot = staticOverlaySnapshotCacheRef.current.get(staticOverlayKey);
          const hasMatchingCachedSnapshot =
            cachedSnapshot !== undefined &&
            cachedSnapshot.width === roundedWidth &&
            cachedSnapshot.height === roundedHeight;

          if (hasMatchingCachedSnapshot) {
            return Promise.resolve({
              snapshot: cachedSnapshot.snapshot,
              rect,
            });
          }
        }

        return (async () => {
          const overlayReady = staticOverlayKey ? await waitForRenderableStaticOverlayAsync(target) : false;

          return new Promise<Snapshot>((resolve, reject) => {
            elementToBlob(target, {
              quality: 1,
              pixelRatio: getExportUiPixelRatio(),
              filter: (node: any) => node.nodeValue !== "straighten",
            })
              .then((blob) => {
                if (blob) {
                  if (staticOverlayKey && overlayReady && hasRenderableStaticOverlayContent(target)) {
                    staticOverlaySnapshotCacheRef.current.set(staticOverlayKey, {
                      snapshot: blob,
                      width: roundedWidth,
                      height: roundedHeight,
                    });
                  }
                  resolve({
                    snapshot: blob,
                    rect: target.getBoundingClientRect(),
                  });
                } else {
                  console.warn("Blob is null, possible rendering issue");
                  reject("Couldn't create blob!!!");
                }
              })
              .catch((error) => {
                reject(error);
              });
          });
        })();
      });
    });

    return Promise.all(mapOverlayPromises);
  }, []);

  return {
    snapshotActiveToolsAsync,
    snapshotToolLabelOverlaysAsync: snapshotToolLabelOverlaysAsync,
  };
};
